import { FC } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { t } from 'i18next';
import dayjs from 'dayjs';
import DownloadIcon from '@mui/icons-material/Download';

import { IColumnDefinition } from '@common/types';

import {
  ISubmissionTableEntries,
  ISubmissionTableFilters,
  useSubmissionsTable,
} from '@views/pages/DataAnalysis/components/Submissions/hooks';
import { theme } from '@store';
import {
  TableNoResultPlaceholder,
  TruncatedTextWithConditionalTooltip,
} from '@components';

export type SubmissionColumnNames =
  | 'download_date'
  | 'date'
  | 'patients'
  | 'pending_patients'
  | 'rejected_patients'
  | 'aligned_patients';

interface ISubmissionsTableProps {
  rows: ISubmissionTableEntries[];
  columns: IColumnDefinition[];
  filters: ISubmissionTableFilters;
  setFilters: (filters: ISubmissionTableFilters) => void;
  totalCount: number;
  loading: boolean;
}

export const SubmissionsTable: FC<ISubmissionsTableProps> = ({
  rows,
  filters,
  columns,
  setFilters,
  totalCount,
  loading,
}) => {
  const {
    sort_by,
    sort_order,
    handleChangeOrder,
    onPageIndexChange,
    onPageSizeChange,
    handleDownload,
    isDownloading,
  } = useSubmissionsTable({
    filters,
    setFilters,
  });

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <FormControl
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'end',
            gap: '8px',
            padding: 2,
            backgroundColor: theme.palette.common.white,
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderBottom: 'none',
          }}
        >
          <DatePicker
            sx={{ width: 200 }}
            label={t('submissions.filters.labels.from')}
            value={dayjs(filters.from)}
            maxDate={dayjs(filters.to)}
            onChange={date => {
              setFilters({ ...filters, from: dayjs(date) });
            }}
          />
          <DatePicker
            sx={{ width: 200 }}
            label={t('submissions.filters.labels.to')}
            minDate={dayjs(filters.from)}
            value={dayjs(filters.to)}
            onChange={date => {
              setFilters({ ...filters, to: dayjs(date) });
            }}
          />
        </FormControl>
      </LocalizationProvider>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 0,
          boxShadow: 'none',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          maxHeight: 'calc(100vh - 620px)',
        }}
      >
        <Table stickyHeader size='small'>
          <TableHead>
            <TableRow
              sx={{
                borderRadius: 0,
                '& .MuiTableCell-head': {
                  paddingY: '1rem',
                  bgcolor: theme.palette.headerBackground.main,
                },
              }}
            >
              {columns.map((col, index) => (
                <TableCell key={`${col.id}-${index}`} align='left'>
                  <Box sx={{ minWidth: col.width }}>
                    {col.sortable ? (
                      <TableSortLabel
                        active={true}
                        direction={col.id === sort_by ? sort_order : 'asc'}
                        onClick={() =>
                          handleChangeOrder(col.id as SubmissionColumnNames)
                        }
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length > 0 && !loading && (
              <>
                {rows.map(row => {
                  const from = dayjs(row.date.from).format('MMM DD,YYYY');
                  const to = dayjs(row.date.to).format('MMM DD,YYYY');

                  return (
                    <TableRow
                      key={row.id}
                      sx={{
                        '& .MuiTableCell-body': {
                          paddingY: '1rem',
                        },
                      }}
                    >
                      <TableCell
                        sortDirection='desc'
                        component='th'
                        scope='row'
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                          }}
                        >
                          <Box
                            sx={{ display: 'flex', flexDirection: 'column' }}
                          >
                            <TruncatedTextWithConditionalTooltip
                              sx={{
                                color: theme => theme.palette.semiBlack.main,
                              }}
                              variant='caption'
                              text={row.download_date}
                            />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align='left' sx={{ maxWidth: '15vh' }}>
                        <TruncatedTextWithConditionalTooltip
                          variant='body2'
                          width='100%'
                          text={row.organization_name}
                        />
                      </TableCell>
                      <TableCell align='left'>
                        <TruncatedTextWithConditionalTooltip
                          variant='body2'
                          width='10rem'
                          text={row.program_name}
                        />
                      </TableCell>
                      <TableCell align='left'>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <TruncatedTextWithConditionalTooltip
                            variant='body2'
                            text={from}
                          />
                          <TruncatedTextWithConditionalTooltip
                            sx={{
                              color: theme => theme.palette.semiBlack.main,
                            }}
                            variant='caption'
                            text={to}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align='left'>
                        <TruncatedTextWithConditionalTooltip
                          variant='body2'
                          text={row.patients}
                        />
                      </TableCell>
                      <TableCell align='left'>
                        <TruncatedTextWithConditionalTooltip
                          variant='body2'
                          text={row.pending_patients}
                        />
                      </TableCell>
                      <TableCell align='left'>
                        <TruncatedTextWithConditionalTooltip
                          variant='body2'
                          text={row.aligned_patients}
                        />
                      </TableCell>
                      <TableCell align='left'>
                        <TruncatedTextWithConditionalTooltip
                          variant='body2'
                          text={row.rejected_patients}
                        />
                      </TableCell>
                      <TableCell align='left'>
                        <IconButton
                          disabled={isDownloading}
                          sx={{
                            '& .MuiIconButton': {
                              padding: '0.5rem',
                            },
                          }}
                          onClick={() => handleDownload(row.id)}
                        >
                          <DownloadIcon
                            sx={{
                              color: isDownloading ? 'gray' : 'primary.main',
                            }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length === 0 && !loading && (
        <Box
          sx={{
            bgcolor: theme.palette.common.white,
            display: 'grid',
            placeItems: 'center',
            width: '100%',
            height: 'calc(100vh - 680px)',
            borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          <TableNoResultPlaceholder />
        </Box>
      )}
      {loading && (
        <Box
          sx={{
            bgcolor: theme.palette.common.white,
            display: 'grid',
            placeItems: 'center',
            width: '100%',
            height: 'calc(100vh - 680px)',
            borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          <CircularProgress size='64px' />
        </Box>
      )}
      <TablePagination
        sx={{
          borderBottom: 0,
          paddingY: '0.75rem',
          bgcolor: 'white',
          border: theme => `1px solid ${theme.palette.grey[300]}`,
        }}
        component='div'
        count={totalCount}
        page={filters.page - 1}
        rowsPerPage={filters.limit}
        onPageChange={onPageIndexChange}
        onRowsPerPageChange={onPageSizeChange}
      />
    </>
  );
};
